import { useLocation } from "react-router-dom";
import * as R from "ramda";
import queryString from "query-string";
import { formatDateForUrl } from "../date.js";

// NOTE this is hardcoded for FloorPlansPage.  If this needs to be used elsewhere
// we need to add a context with defaults passed in
const DEFAULTS = {
  bedrooms: null,
  bathrooms: null,
  floors: null,
  attributes: [],
  page: 1,
  price: null,
  available_on: formatDateForUrl(new Date()),
};

export default function useSearchValues() {
  const location = useLocation();

  const { search } = location;

  const searchValues = {
    ...DEFAULTS,
    ...parseSearch(search),
  };

  // remove any values that are the default values
  // (we don't want to clutter the url)
  const removeDefaultValues = R.compose(
    R.fromPairs,
    R.reject(pair => {
      const [key, value] = pair;
      return DEFAULTS[key] === value;
    }),
    R.toPairs,
  );

  // function to get an href for some new values
  // eg. with url of /?bedrooms=1
  // getLocationFor({ page: 2 }) => /?bedrooms=1&page=2
  const getLocationFor = values => {
    return {
      ...location,
      search: R.compose(
        s => "?" + s,
        obj => queryString.stringify(obj, { arrayFormat: "bracket" }),
        removeDefaultValues,
        R.mergeLeft(values),
        parseSearch,
      )(search),
    };
  };

  const apiQueryString = R.compose(
    s => "?" + s,
    obj => queryString.stringify(obj, { arrayFormat: "bracket" }),
    transformPrice,
    // page is base 0 for the api call
    R.over(R.lensProp("page"), page => {
      return R.isNil(page) ? 0 : page - 1;
    }),
    parseSearch,
  )(search);

  return { searchValues, getLocationFor, apiQueryString };
}

// transform the price=1000-1500 in the url to the correct format for the api call
// ?price[min]=1000&price[max]=1500
function transformPrice(search) {
  const { price } = search;

  if (!price) {
    return search;
  }

  // the api needs price[min] and price[max]
  const apiValues = R.compose(
    R.zipObj(["price[min]", "price[max]"]),
    R.split("-"),
    R.unless(R.is(String), R.toString), // might be 5000, which will be parsed to a number
  )(price);

  // remove price and add price[min]/price[max]
  return R.compose(R.mergeLeft(apiValues), R.omit(["price"]))(search);
}

// https://github.com/sindresorhus/query-string/blob/master/index.js#L207-L215
function parseValue(value) {
  if (
    !Number.isNaN(Number(value)) &&
    typeof value === "string" &&
    value.trim() !== ""
  ) {
    return Number(value);
  }
  else if (Array.isArray(value)) {
    return value.map(Number);
  }
  else if (
    value !== null &&
    (value.toLowerCase() === "true" || value.toLowerCase() === "false")
  ) {
    return value.toLowerCase() === "true";
  }

  return value;
}

const parseSearch = search => {
  return R.compose(R.map(parseValue), string =>
    queryString.parse(string, {
      arrayFormat: "bracket",
    }),
  )(search);
};
